import { useEffect, useRef, useState } from 'react';
import { Storage, adoptServerWrite } from '../lib/storage';
import { useServerApplied } from './serverApplied';
import type { SetToast } from './toast';

/** The two keys a series lives in. */
export type SeriesKey = 'active_series_v1' | 'paused_series_v1';

export interface UseSeriesDeps {
  /** The store's toast slot — where a failed series write is reported (#1311). */
  setToast: SetToast;
}

/**
 * What a stored series key holds, as the in-memory slice would hold it — or
 * `undefined` when the stored text is not a document this slice can take.
 *
 * The same rule the mount loader applies (`useInitialLoad`): an active series
 * is an object whose `type` is `'seriespill'`, and `''` is «no active series».
 * `undefined` is not «nothing stored»: an unreadable row must never replace a
 * live series on screen with an empty one (#413).
 */
function parseStored(key: SeriesKey, value: string | null): any {
  if (key === 'active_series_v1') {
    if (!value) return null;
    try {
      const doc = JSON.parse(value);
      return doc && typeof doc === 'object' && doc.type === 'seriespill' ? doc : undefined;
    } catch {
      return undefined;
    }
  }
  if (value == null || value === '') return [];
  try {
    const list = JSON.parse(value);
    return Array.isArray(list) ? list : undefined;
  } catch {
    return undefined;
  }
}

// ─────────────────────────────────────────────────────────────────────
// useSeries — the seriespill domain, lifted out of the Padel24AppMain god
// component (see docs/history/DECOMPOSITION_PLAN.md §5, PR-G; HANDOVER §13.20 / R1).
//
// THIS IS THE MOST DANGEROUS LIFT. It owns TWO §13.20 R1 blanket-persist
// slices. R1 says each such slice is a TRIO that MUST live together:
//   (1) the useState slice,
//   (2) a useRef loaded-flag (starts false),
//   (3) a blanket-persist useEffect that writes Storage on EVERY state change
//       but bails early with `if (!flag.current) return;` so it NEVER writes
//       before the initial load has flipped the flag.
// Splitting state from its flag/effect overwrites saved data with the empty
// init-state — silent loss of tournament/series data on a live platform.
//
//   • activeSeries  ('active_series_v1', TENANT-SCOPED) — the single live serie
//                   (separate track; never parked by new tournaments). useState
//                   init is `null`; the empty case writes '' (the atomic loader
//                   treats a falsy `.value` as absent — do NOT change to skip).
//   • pausedSeries  ('paused_series_v1') — list of paused-serie snapshots.
//
// Both trios were copied VERBATIM from the former monolith — keys and the ''
// write for the null activeSeries case are unchanged. Do NOT "fix" or
// normalize them. The ONE deliberate departure is the error handling: a failed
// write used to be logged and nothing else, and is now reported to the person
// with an offer to reload the stored version (#1311 — the same pattern as the
// tournament keys, #1305; see `reportSeriesSaveFailure` below).
//
// WHAT STAYS IN APP (this hook EXPOSES state + setter + ref for each so App can
// keep doing it):
//   • The INITIAL load — it stays in the App shell's ATOMIC mount loader (one
//     large useEffect that reads ~13 domains in one pass; DECOMPOSITION_PLAN
//     principle #1 / R1). That loader reads both keys and flips
//     `activeSeriesLoaded.current = true` / `pausedSeriesLoaded.current = true`.
//     Per the deliberate in/out-of-`cancelled` asymmetry (R1), BOTH series flags
//     flip OUTSIDE the `cancelled` guard — that asymmetry lives in App, not here.
//     Do NOT add a load effect to this hook.
//   • The explicit extra-safety double-write mutators updateActiveSeries /
//     persistSeries / persistPausedSeries / handlePauseSeries etc. — they write
//     these keys explicitly AND call the exposed setters; the blanket-persist
//     effects above are the second, defence-in-depth write.
//   • serieHistoryFocus (a NON-persisted UI slice that sat between the two
//     slices in the former monolith) — it is not part of either trio; it stays
//     in App.
//
// src/ imports ONLY from src/ (Storage from '../lib/storage').
// ─────────────────────────────────────────────────────────────────────

export function useSeries({ setToast }: UseSeriesDeps) {
  // Series play runs in a separate track and is never parked by new tournaments
  // (one active series for now; the architecture can later become a list).
  const [activeSeries, setActiveSeries] = useState(null);
  // Paused series: a list of archived snapshots that can be resumed. A series
  // that is paused is hidden from the home screen, but its matches/standings are
  // preserved and can be brought back in. Differs from "retirement", which is
  // final (archived).
  const [pausedSeries, setPausedSeries] = useState([]);
  // Blanket safety for series: persists the active + paused series on every change,
  // independent of call path (same principle as tournaments). updateActiveSeries /
  // persistPausedSeries already do this explicitly; this is an extra guarantee.
  const activeSeriesLoaded = useRef(false);
  const activeFromServer = useServerApplied<any>();
  // Set by a reload that found «no active series» stored — see the effect.
  const reloadedEmpty = useRef(false);
  useEffect(() => {
    if (!activeSeriesLoaded.current) return;
    // The server stored this one itself (#405 — the season transition commits
    // and rewrites the document in one transaction). Writing it back would be
    // redundant, would carry a stale `ifVersion`, and would be the browser
    // writing the session blob again. By identity, so it suppresses exactly
    // one value and the next ordinary change persists as always.
    if (activeFromServer.consume(activeSeries)) return;
    // A reload that found «no active series» stored. `useServerApplied` never
    // suppresses null (it must not get stuck), so the empty case has its own
    // one-shot flag: writing '' back would be a write nobody asked for.
    if (activeSeries == null && reloadedEmpty.current) {
      reloadedEmpty.current = false;
      return;
    }
    reloadedEmpty.current = false;
    Storage.set('active_series_v1', activeSeries ? JSON.stringify(activeSeries) : '').catch((e) =>
      reportSeriesSaveFailure('active_series_v1', e),
    );
    // `reportSeriesSaveFailure` reads only refs and stable setters, so it is
    // deliberately not a dependency: a new closure each render must not
    // re-fire a write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSeries, activeFromServer]);
  const pausedSeriesLoaded = useRef(false);
  const pausedFromServer = useServerApplied<any[]>();
  useEffect(() => {
    if (!pausedSeriesLoaded.current) return;
    if (pausedFromServer.consume(pausedSeries)) return; // the reload took the stored list
    Storage.set('paused_series_v1', JSON.stringify(pausedSeries)).catch((e) =>
      reportSeriesSaveFailure('paused_series_v1', e),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pausedSeries, pausedFromServer]);

  // ──── A SERIES WRITE THAT DID NOT LAND (#1311) ────
  //
  // The tournament pattern (#1305, `reportTournamentSaveFailure` in
  // useTournaments), for the series keys. Both persist effects above and the
  // explicit writers in useSeriesActions (`persistSeries` /
  // `persistPausedSeries`) used to catch a failed write and only log it, so
  // the screen kept a series the database did not have — including Banepad
  // live scores and results, which travel in `active_series_v1`.
  //
  // A 409 (another session moved the key), a write refused locally as
  // must-refresh, and an ambiguous 5xx or dropped connection are not told
  // apart: all mean «this was not saved», and all leave the key must-refresh
  // (lib/storage.ts), so no write to it goes out until it has been re-read.
  //
  // THE OFFER IS THE STORED VERSION. «Last inn på nytt» reads each failed key
  // and puts what is stored on screen. It never re-sends the in-memory copy:
  // that copy is what was refused. The effects skip the reloaded value
  // because it is already stored. Dismissing keeps the screen as it is, and
  // the next write to the key is refused locally and reported again.
  const failedKeys = useRef(new Set<SeriesKey>());
  const reloadStoredSeries = async () => {
    setToast(null);
    const keys = [...failedKeys.current];
    for (const key of keys) {
      // `read`, not `get`: `get` answers null for a failed request too, and a
      // failed reload must leave the screen alone rather than empty it.
      const res = await Storage.read(key);
      const stored =
        res.status === 'failed'
          ? undefined
          : parseStored(key, res.status === 'ok' ? res.value : null);
      if (stored === undefined) {
        reportSeriesSaveFailure(key, new Error(`reload of ${key} failed (${res.status})`));
        continue;
      }
      failedKeys.current.delete(key);
      if (key === 'active_series_v1') {
        if (stored === null) reloadedEmpty.current = true;
        else activeFromServer.mark(stored);
        setActiveSeries(stored);
      } else {
        pausedFromServer.mark(stored);
        setPausedSeries(stored);
      }
    }
  };
  /**
   * Report a series write that did not land, and offer the stored version.
   * The error goes to the console for diagnosis; the person gets the toast.
   */
  function reportSeriesSaveFailure(key: SeriesKey, error: unknown) {
    console.error(`[Padel24] Serien ble ikke lagret (${key}):`, error);
    failedKeys.current.add(key);
    setToast({
      type: 'error',
      message: '',
      messageKey: 'series.saveFailed.message',
      action: {
        labelKey: 'series.saveFailed.reload',
        run: () => void reloadStoredSeries(),
      },
    });
  }

  /**
   * Take a series the SERVER has already applied and stored (#405).
   *
   * Storage-LAST: the write happened server-side, inside the transaction that
   * committed the class's ELO and moved its finished season into history.
   * There is nothing left to persist, only a version to adopt so this
   * browser's next ordinary write is not refused for a change it asked for.
   *
   * Unlike the paused-tournament case, the client genuinely holds what was
   * stored — the response carries the whole series document — so the version
   * can be taken.
   */
  const adoptServerSeries = (next: any, storage?: { key: string; version: number }) => {
    activeFromServer.mark(next);
    if (storage) adoptServerWrite(storage.key, JSON.stringify(next), storage.version);
    setActiveSeries(next);
  };

  return {
    adoptServerSeries,
    activeSeries,
    pausedSeries,
    // Setters + the loaded-flag refs are exposed for the App shell's atomic mount
    // loader (which flips both refs after reading the values, OUTSIDE the
    // `cancelled` guard) and for the App-side double-write mutators
    // (updateActiveSeries / persistPausedSeries / handlePauseSeries / …) that
    // call the setters. The blanket-persist effects above stay owned here.
    setActiveSeries,
    activeSeriesLoaded,
    setPausedSeries,
    pausedSeriesLoaded,
    // #1311 — for useSeriesActions' explicit writers of both keys.
    reportSeriesSaveFailure,
  };
}
